/**
 * Iter 253z-14 · Project Intelligence dashboard.
 *
 * Owners see the whole workspace; project-owners see only their scoped view.
 * Traffic-light per project, wins/blockers, missed updates, AI-narrated
 * exec briefs, one-click digest send + auto-nudge tasks, configurable
 * digest hours + missed-update penalty.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import {
  RefreshCw, Send, Settings, Loader2, TrendingUp, CheckCircle2,
  AlertTriangle, Flame, MessageSquare, ClipboardCheck, Sparkles, PlusCircle,
  Circle, Users, Mail, FileText, Rocket, TrendingDown, Zap, Snail, Share2,
  Download, Search as SearchIcon, History, Calendar as CalendarIcon, GitCompareArrows,
  ListChecks, MessageSquarePlus, MoonStar,
} from "lucide-react";
import MultiSelect from "@/components/ui/multi-select";
import PostProjectUpdateDialog from "@/components/project/PostProjectUpdateDialog";
import ProjectTasksPopup from "@/components/Client/ProjectTasksPopup";

const TRAFFIC = {
  green: { chip: "chip-approved", tone: "border-emerald-400 bg-emerald-50/50", dot: "bg-emerald-500", label: "Green" },
  amber: { chip: "chip-pending", tone: "border-amber-400 bg-amber-50/50", dot: "bg-amber-500", label: "Amber" },
  red:   { chip: "chip-rejected", tone: "border-rose-400 bg-rose-50/50", dot: "bg-rose-500", label: "Red" },
};

export default function ProjectIntelligence() {
  const { activeWorkspaceId } = useAuth();
  const [snap, setSnap] = useState(null);
  const [busy, setBusy] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [sending, setSending] = useState(false);
  const [nudging, setNudging] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [filter, setFilter] = useState("all");   // all | red | amber | green | missed
  const [backfilling, setBackfilling] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testingSend, setTestingSend] = useState(false);
  const [trend, setTrend] = useState(null);   // { projects: [{project_id, series, drifting_red}] }
  const [showClientDigest, setShowClientDigest] = useState(false);
  // Feb 2026 · Snapshot history — date picker + per-project + exec-summary popups
  const [selectedDate, setSelectedDate] = useState("");   // "" = today (live snapshot)
  const [historyDates, setHistoryDates] = useState([]);   // [{date, headline, totals}, …]
  const [historyProject, setHistoryProject] = useState(null);  // {id, name}
  const [showExecHistory, setShowExecHistory] = useState(false);
  const [showCompare, setShowCompare] = useState(false);   // Feb 2026 · WoW compare
  // R148g · Feb 2026 — Multi-select filters + per-card action popups
  const [selClients, setSelClients] = useState([]);
  const [selBrands, setSelBrands] = useState([]);
  const [selProjects, setSelProjects] = useState([]);
  const [selOwners, setSelOwners] = useState([]);
  const [selStatuses, setSelStatuses] = useState([]);
  const [clientDir, setClientDir] = useState([]);   // [{id,name}]
  const [brandDir, setBrandDir] = useState([]);
  const [postUpdateFor, setPostUpdateFor] = useState(null);   // project row
  const [tasksPopupFor, setTasksPopupFor] = useState(null);   // project row

  const load = async (force = false, dateParam = null) => {
    if (!activeWorkspaceId) return;
    const dt = dateParam !== null ? dateParam : selectedDate;
    setBusy(true);
    try {
      // Iter 253z-18 · Refresh must go all the way — rebuild snapshot with
      // AI briefs + executive summary. Fixes the reported "nothing updates
      // when I click Refresh" bug.
      // Feb 2026: we skip the rebuild when a past date is selected — that
      // request should surface the report AS IT WAS on that day.
      if (force && !dt) {
        try {
          await api.post(`/workspaces/${activeWorkspaceId}/project-intel/refresh`, null,
                        { params: { include_ai: true } });
        } catch (e) { /* fall through — dashboard fetch still shows cached */ }
      }
      const dashParams = { include_ai: false, force_refresh: false };
      if (dt) dashParams.date = dt;
      const [dash, tr] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/project-intel/dashboard`, {
          params: dashParams,
        }),
        api.get(`/workspaces/${activeWorkspaceId}/project-intel/trend`, {
          params: { days: 7 },
        }).catch(() => ({ data: null })),
      ]);
      setSnap(dash.data);
      setTrend(tr.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load")); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(false); /* eslint-disable-next-line */ }, [activeWorkspaceId]);

  // R148g · Feb 2026 — Fetch client + brand directories once so the
  // multi-select filters can show proper names (project rows only carry
  // client_id / brand_id).
  useEffect(() => {
    if (!activeWorkspaceId) return;
    Promise.all([
      api.get(`/workspaces/${activeWorkspaceId}/clients`).catch(() => ({ data: [] })),
      api.get(`/workspaces/${activeWorkspaceId}/brands`).catch(() => ({ data: [] })),
    ]).then(([c, b]) => {
      const cl = Array.isArray(c.data) ? c.data : (c.data?.items || []);
      const br = Array.isArray(b.data) ? b.data : (b.data?.items || []);
      setClientDir(cl.map((x) => ({ id: x.id, name: x.name || x.title || "—" })));
      setBrandDir(br.map((x) => ({ id: x.id, name: x.name || "—" })));
    });
  }, [activeWorkspaceId]);

  // Feb 2026 · fetch list of dates that have a snapshot to power the picker
  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/project-intel/history-dates`, { params: { limit: 120 } })
      .then((r) => setHistoryDates(r.data?.rows || []))
      .catch(() => setHistoryDates([]));
  }, [activeWorkspaceId, snap?.generated_at]);

  const onDateChange = (v) => {
    // "" or "today" load the live/today snapshot; anything else loads that
    // day's stored report exactly as it was generated.
    const target = v === "today" ? "" : v;
    setSelectedDate(target);
    load(false, target);
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/project-intel/refresh`, null, { params: { include_ai: true } });
      toast.success("Snapshot rebuilt with AI briefs");
      await load(false);
    } catch (e) { toast.error(apiErrorMessage(e, "Refresh failed")); }
    finally { setRefreshing(false); }
  };

  const sendDigest = async (kind) => {
    if (!window.confirm(`Send the ${kind === "preclose" ? "8 PM pre-close" : "10 PM final"} digest email now?`)) return;
    setSending(true);
    try {
      await api.post(`/workspaces/${activeWorkspaceId}/project-intel/send-digest`, { kind, include_ai: true });
      toast.success("Digest queued — recipients will get it within a minute");
    } catch (e) { toast.error(apiErrorMessage(e, "Send failed")); }
    finally { setSending(false); }
  };

  const nudgeTasks = async () => {
    if (!window.confirm("Create an 'Update status' task for every project owner missing today's update?")) return;
    setNudging(true);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/project-intel/nudge-tasks`);
      toast.success(`Nudged ${data.tasks_created_for || 0} owner(s)`);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setNudging(false); }
  };

  const runBackfill = async (dryRun) => {
    setBackfilling(true);
    try {
      const { data } = await api.post(`/workspaces/${activeWorkspaceId}/project-intel/backfill-owners`, { dry_run: dryRun });
      const hist = Object.entries(data.sources_hist || {})
        .map(([k, v]) => `${k}=${v}`).join(", ");
      toast.success(
        dryRun
          ? `Preview: ${data.candidates} project(s) would be assigned · ${hist}`
          : `Backfilled ${data.applied}/${data.candidates} project(s) · ${hist}`,
      );
      if (!dryRun) await load(true);
    } catch (e) { toast.error(apiErrorMessage(e, "Backfill failed")); }
    finally { setBackfilling(false); }
  };

  const sendTest = async () => {
    if (!testEmail.trim()) { toast.error("Enter your email"); return; }
    setTestingSend(true);
    try {
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/project-intel/send-test-digest`,
        { to: testEmail.trim(), kind: "final" });
      if (data.ok) toast.success(`Test digest sent to ${testEmail} — check inbox`);
      else toast.error(`Send failed: ${data.error || "provider error"}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed")); }
    finally { setTestingSend(false); }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/workspaces/${activeWorkspaceId}/project-intel/export-pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `project-intel-${snap?.date || "today"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) { toast.error("PDF export failed"); }
  };

  const filtered = useMemo(() => {
    if (!snap?.projects) return [];
    let rows = snap.projects;
    // Traffic chip filter (existing)
    if (filter !== "all") {
      rows = filter === "missed"
        ? rows.filter((p) => !p.has_update_today)
        : rows.filter((p) => p.traffic === filter);
    }
    // R148g · Multi-select filters — OR within group, AND across groups.
    if (selClients.length) rows = rows.filter((p) => selClients.includes(p.client_id));
    if (selBrands.length)  rows = rows.filter((p) => selBrands.includes(p.brand_id));
    if (selProjects.length) rows = rows.filter((p) => selProjects.includes(p.id));
    if (selOwners.length)  rows = rows.filter((p) => selOwners.includes(p.owner_id));
    if (selStatuses.length) rows = rows.filter((p) => selStatuses.includes((p.status || "active").toLowerCase()));
    return rows;
  }, [snap, filter, selClients, selBrands, selProjects, selOwners, selStatuses]);

  // Filter option lists — derived from the current snapshot so that
  // options only expose things the user actually has projects for.
  const filterOpts = useMemo(() => {
    const clientMap = Object.fromEntries(clientDir.map((c) => [c.id, c.name]));
    const brandMap = Object.fromEntries(brandDir.map((b) => [b.id, b.name]));
    const c = new Map(), b = new Map(), pr = new Map(), ow = new Map(), st = new Map();
    for (const p of (snap?.projects || [])) {
      if (p.client_id) c.set(p.client_id, clientMap[p.client_id] || p.client_name || "(unnamed client)");
      if (p.brand_id)  b.set(p.brand_id, brandMap[p.brand_id]   || p.brand_name  || "(unnamed brand)");
      pr.set(p.id, p.name || "(untitled)");
      if (p.owner_id)  ow.set(p.owner_id, p.owner_name || "(unnamed owner)");
      const s = (p.status || "active").toLowerCase();
      st.set(s, s.replace(/_/g, " "));
    }
    const toOpts = (m) => Array.from(m, ([value, label]) => ({ value, label }));
    return {
      clients: toOpts(c),
      brands:  toOpts(b),
      projects: toOpts(pr),
      owners:  toOpts(ow),
      statuses: toOpts(st),
    };
  }, [snap, clientDir, brandDir]);

  const clearAllFilters = () => {
    setSelClients([]); setSelBrands([]); setSelProjects([]);
    setSelOwners([]); setSelStatuses([]); setFilter("all");
  };
  const anyFilterActive = filter !== "all" || selClients.length || selBrands.length
    || selProjects.length || selOwners.length || selStatuses.length;

  const trendMap = useMemo(() => {
    const m = {};
    for (const row of (trend?.projects || [])) {
      m[row.project_id] = row;
    }
    return m;
  }, [trend]);

  const driftingRedCount = useMemo(() => (
    (trend?.projects || []).filter((p) => p.drifting_red).length
  ), [trend]);

  const isAdmin = snap?.scope === "workspace";

  return (
    <>
      <PageHeader
        eyebrow="Intelligence"
        title="Project Intelligence"
        description={
          isAdmin
            ? "Every project's day-so-far — wins, blockers, missed updates, AI-narrated briefs. Digests fire at 8 PM (pre-close) and 10 PM (final)."
            : "Your projects for today. Post a status update to keep your streak alive — missing an update costs you leaderboard points."
        }
        testid="project-intel-header"
        actions={
          // R68 · Wrap onto a second line on narrow screens so the
          // rightmost buttons (Backfill, Compare, Refresh+AI) don't
          // float off-screen. Previously a single-line flex clipped on
          // any laptop <1440px.
          <div className="flex items-center gap-2 flex-wrap justify-end max-w-full">
            {/* Feb 2026 · Date picker to load past reports as-generated */}
            <Select value={selectedDate || "today"} onValueChange={onDateChange}>
              <SelectTrigger className="h-8 w-[170px] rounded-none text-xs" data-testid="pi-date-select">
                <CalendarIcon className="w-3 h-3 mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today (live)</SelectItem>
                {historyDates.map((d) => (
                  <SelectItem key={d.date} value={d.date} data-testid={`pi-date-opt-${d.date}`}>
                    {d.date}{d.totals ? ` · ${d.totals.reds || 0}R/${d.totals.ambers || 0}A` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button variant="outline" size="sm" onClick={() => setShowCompare(true)}
                     className="rounded-none h-8 text-xs" data-testid="pi-compare-btn"
                     title="Compare two snapshots (week-over-week or any two dates)">
              <GitCompareArrows className="w-3 h-3 mr-1" /> Compare
            </Button>

            <HoverCard openDelay={100}>
              <HoverCardTrigger asChild>
                <Button variant="outline" size="sm" onClick={() => load(true)} disabled={busy} className="rounded-none h-8 text-xs" data-testid="pi-refresh-cache" title="Rebuild snapshot with AI briefs + executive summary">
                  <RefreshCw className={`w-3 h-3 mr-1 ${busy ? "animate-spin" : ""}`} /> {busy ? "Refreshing…" : "Refresh + AI"}
                </Button>
              </HoverCardTrigger>
              <HoverCardContent className="w-80 text-xs" data-testid="pi-refresh-help">
                <div className="font-semibold mb-1 inline-flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 text-primary" /> Refresh + AI
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  Recomputes today&apos;s snapshot from the raw activity (tasks,
                  comments, updates, hours logged) and <b>re-runs the AI</b> to
                  regenerate the per-project briefs and the executive summary at
                  the top of this page. Use it when you&apos;ve just posted a
                  round of status updates and nothing on the dashboard reflects
                  them yet.
                </p>
                <div className="mt-2 pt-2 border-t border-border text-[10px] text-muted-foreground">
                  Takes ~30–60s · Uses the Emergent LLM key
                </div>
              </HoverCardContent>
            </HoverCard>
            {isAdmin && (
              <>
                <HoverCard openDelay={100}>
                  <HoverCardTrigger asChild>
                    <Button variant="outline" size="sm" onClick={refresh} disabled={refreshing} className="rounded-none h-8 text-xs" data-testid="pi-rebuild">
                      {refreshing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />}
                      Rebuild + AI
                    </Button>
                  </HoverCardTrigger>
                  <HoverCardContent className="w-80 text-xs" data-testid="pi-rebuild-help">
                    <div className="font-semibold mb-1 inline-flex items-center gap-1.5">
                      <Sparkles className="w-3.5 h-3.5 text-primary" /> Rebuild + AI (admin)
                    </div>
                    <p className="text-muted-foreground leading-relaxed">
                      A full-strength rebuild that discards the cached snapshot,
                      recomputes every project&apos;s traffic light from the
                      ground up, and re-generates AI briefs for the top projects
                      + the executive summary. It also <b>writes the fresh
                      snapshot back</b> so tonight&apos;s digest ships this
                      version.
                    </p>
                    <div className="mt-2 pt-2 border-t border-border text-[10px] text-muted-foreground">
                      Use before firing a manual digest so the email matches what admins see on-screen.
                    </div>
                  </HoverCardContent>
                </HoverCard>
                <Button variant="outline" size="sm" onClick={() => sendDigest("preclose")} disabled={sending} className="rounded-none h-8 text-xs" data-testid="pi-send-preclose">
                  <Send className="w-3 h-3 mr-1" /> Send 8 PM
                </Button>
                <Button size="sm" onClick={() => sendDigest("final")} disabled={sending} className="rounded-none h-8 text-xs" data-testid="pi-send-final">
                  {sending ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                  Send 10 PM
                </Button>
                <Button variant="outline" size="sm" onClick={nudgeTasks} disabled={nudging} className="rounded-none h-8 text-xs" data-testid="pi-nudge-tasks">
                  {nudging ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <PlusCircle className="w-3 h-3 mr-1" />}
                  Create nudge tasks
                </Button>
                <Button variant="outline" size="sm" onClick={downloadPdf} className="rounded-none h-8 text-xs" data-testid="pi-export-pdf">
                  <FileText className="w-3 h-3 mr-1" /> PDF
                </Button>
                <Button variant="outline" size="sm" onClick={() => setShowClientDigest(true)} className="rounded-none h-8 text-xs" data-testid="pi-client-digest-open">
                  <Share2 className="w-3 h-3 mr-1" /> Client digest
                </Button>
                <Button variant="outline" size="sm" onClick={() => runBackfill(false)} disabled={backfilling} className="rounded-none h-8 text-xs" data-testid="pi-backfill-owners" title="Assign an owner to every project without one (uses updated_by, client owner, member, or workspace owner)">
                  {backfilling ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Users className="w-3 h-3 mr-1" />}
                  Backfill owners
                </Button>
                <Button variant="ghost" size="sm" onClick={() => setShowConfig(true)} className="rounded-none h-8 text-xs" data-testid="pi-config-open">
                  <Settings className="w-3 h-3 mr-1" /> Config
                </Button>
              </>
            )}
          </div>
        }
      />
      <div className="px-4 sm:px-6 md:px-10 pb-10 space-y-5">
        {selectedDate && (
          <div className="border border-indigo-400 bg-indigo-50/40 px-3 py-2 text-xs flex items-center justify-between" data-testid="pi-past-banner">
            <span>
              <b>Viewing snapshot from {selectedDate}</b> — this is exactly the
              report as it was generated on that day. Live actions (Rebuild,
              Send digest) are disabled while browsing history.
            </span>
            <button onClick={() => onDateChange("today")} className="text-primary underline text-[11px]" data-testid="pi-past-banner-back">
              Back to today
            </button>
          </div>
        )}
        {busy && !snap ? (
          <div className="text-sm text-muted-foreground">Building today&apos;s snapshot…</div>
        ) : !snap ? (
          <div className="text-sm text-muted-foreground">No snapshot yet.</div>
        ) : (
          <>
            {/* Headline banner */}
            <div className="border border-primary bg-primary/5 p-5" data-testid="pi-headline">
              <div className="label-eyebrow mb-1">State of the union · {snap.date}</div>
              <div className="text-xl font-bold" style={{ fontFamily: "var(--font-heading)" }}>
                {snap.headline}
              </div>
              <div className="text-[11px] text-muted-foreground mt-1">Generated {snap.generated_at?.slice(0, 16).replace("T", " ")} UTC · {snap.scope === "workspace" ? "Workspace view" : "Your projects only"}</div>
            </div>

            {/* Iter 253z-18 · Executive AI summary — owner/admin only */}
            {isAdmin && snap.executive_summary && (
              <div className="border-2 border-primary/40 bg-gradient-to-br from-primary/5 to-transparent p-5 relative" data-testid="pi-exec-summary">
                {isAdmin && (
                  <button
                    type="button"
                    onClick={() => setShowExecHistory(true)}
                    className="absolute top-2 right-2 inline-flex items-center gap-1 border border-border bg-background/80 hover:bg-muted px-1.5 py-0.5 text-[10px]"
                    title="View past executive summaries"
                    data-testid="pi-exec-history-btn"
                  >
                    <History className="w-3 h-3" /> History
                  </button>
                )}
                <div className="label-eyebrow inline-flex items-center gap-1.5 mb-2 text-primary">
                  <Sparkles className="w-3.5 h-3.5" /> Executive briefing
                </div>
                <div className="text-sm leading-relaxed whitespace-pre-wrap">{snap.executive_summary}</div>
              </div>
            )}

            {/* Iter 253z-18 · Movers · Laggers · People — admin only */}
            {isAdmin && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pi-movers-laggers">
                <MoverList title="Top movers" icon={Rocket} tone="emerald"
                            items={(snap.movers || []).map((m) => ({
                              title: m.name, sub: `${m.owner_name || "—"} · ${m.wins} wins · ${m.hours}h`,
                            }))}
                            empty="No projects moved today" testid="pi-movers" />
                <MoverList title="Laggers" icon={TrendingDown} tone="rose"
                            items={(snap.laggers || []).map((l) => ({
                              title: l.name, sub: `${l.owner_name || "—"} · ${l.days_without_update}d silent · ${l.overdue_count} overdue`,
                            }))}
                            empty="Everyone on track" testid="pi-laggers" />
                <MoverList title="Fast actors" icon={Zap} tone="emerald"
                            items={(snap.fast_actors || []).slice(0, 6).map((u) => ({
                              title: u.name || "—",
                              sub: `score ${u.score} · ${u.tasks_done} done · ${u.hours}h · ${u.updates} updates`,
                            }))}
                            empty="No activity yet" testid="pi-fast-actors" />
                <MoverList title="Lazy / missed" icon={Snail} tone="amber"
                            items={(snap.lazy_actors || []).slice(0, 6).map((u) => ({
                              title: u.name || "—",
                              sub: `${u.missed_projects} project(s) missed today`,
                            }))}
                            empty="No stragglers today" testid="pi-lazy-actors" />
              </div>
            )}

            {/* KPI strip */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2" data-testid="pi-kpis">
              <Kpi label="Projects" value={snap.totals.projects} icon={ClipboardCheck} />
              <Kpi label="Updated today" value={`${snap.totals.updated_today}/${snap.totals.projects}`} icon={CheckCircle2} tone="green" />
              <Kpi label="Missed" value={snap.totals.missed_updates} icon={AlertTriangle} tone={snap.totals.missed_updates ? "amber" : "muted"} />
              <Kpi label="Wins" value={snap.totals.wins} icon={TrendingUp} tone="green" />
              <Kpi label="Blockers" value={snap.totals.blockers} icon={Flame} tone={snap.totals.blockers ? "red" : "muted"} />
              <Kpi label="Reds / Ambers" value={`${snap.totals.reds} / ${snap.totals.ambers}`} icon={Circle} tone={snap.totals.reds ? "red" : (snap.totals.ambers ? "amber" : "muted")} />
            </div>

            {/* Filter chips */}
            <div className="flex items-center gap-1 flex-wrap" data-testid="pi-filter-chips">
              {[
                ["all", `All (${snap.projects.length})`],
                ["red", `Red (${snap.totals.reds})`],
                ["amber", `Amber (${snap.totals.ambers})`],
                ["green", `Green (${snap.projects.length - snap.totals.reds - snap.totals.ambers})`],
                ["missed", `Missed today (${snap.totals.missed_updates})`],
              ].map(([v, label]) => (
                <button key={v} onClick={() => setFilter(v)}
                  className={`text-[10px] px-2 py-1 border ${filter === v ? "border-primary bg-primary text-primary-foreground" : "border-border hover:border-primary"}`}
                  data-testid={`pi-filter-${v}`}>
                  {label}
                </button>
              ))}
            </div>

            {/* R148g · Multi-select filter row — sorted, searchable, multi-pick */}
            <div className="border border-border bg-muted/30 p-2 flex flex-wrap items-center gap-2" data-testid="pi-multi-filters">
              <span className="label-eyebrow text-[10px] text-muted-foreground shrink-0">Filter by</span>
              <MultiSelect
                options={filterOpts.clients}
                value={selClients}
                onChange={setSelClients}
                allLabel="All clients"
                placeholder="Clients"
                width="w-40"
                testid="pi-mf-clients"
              />
              <MultiSelect
                options={filterOpts.brands}
                value={selBrands}
                onChange={setSelBrands}
                allLabel="All brands"
                placeholder="Brands"
                width="w-40"
                testid="pi-mf-brands"
              />
              <MultiSelect
                options={filterOpts.projects}
                value={selProjects}
                onChange={setSelProjects}
                allLabel="All projects"
                placeholder="Projects"
                width="w-44"
                testid="pi-mf-projects"
              />
              <MultiSelect
                options={filterOpts.owners}
                value={selOwners}
                onChange={setSelOwners}
                allLabel="All owners"
                placeholder="Owners"
                width="w-40"
                testid="pi-mf-owners"
              />
              <MultiSelect
                options={filterOpts.statuses}
                value={selStatuses}
                onChange={setSelStatuses}
                allLabel="All statuses"
                placeholder="Status"
                width="w-36"
                testid="pi-mf-status"
              />
              {anyFilterActive ? (
                <button onClick={clearAllFilters}
                  className="text-[10px] uppercase tracking-widest text-destructive hover:underline ml-auto"
                  data-testid="pi-mf-clear-all">
                  Clear all filters
                </button>
              ) : (
                <span className="ml-auto text-[10px] text-muted-foreground">
                  Showing all {snap.projects.length} projects
                </span>
              )}
            </div>

            {/* Missed updates callout */}
            {snap.missed_updates?.length > 0 && (
              <section className="border border-amber-400 bg-amber-50/40 p-4 space-y-2" data-testid="pi-missed-callout">
                <div className="flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-700" />
                  <b className="text-sm">Missed updates today</b>
                  <span className="ml-auto text-[10px] text-muted-foreground">
                    Each missed update ⇒ {snap.config?.missed_penalty_points ?? -5} pts on the leaderboard at 10 PM.
                  </span>
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-1 text-xs">
                  {snap.missed_updates.map((m) => (
                    <li key={m.project_id} className="flex items-center gap-2 truncate">
                      <span className="w-1.5 h-1.5 bg-amber-500 rounded-full" />
                      <b>{m.project_name}</b> — {m.owner_name || "no owner"} · {m.days_without_update}d
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Iter 253z-15 · Drifting-red early-warning callout */}
            {driftingRedCount > 0 && (
              <section className="border border-rose-400 bg-rose-50/40 p-4 flex items-center gap-3" data-testid="pi-drifting-callout">
                <Flame className="w-5 h-5 text-rose-600 shrink-0" />
                <div className="text-sm">
                  <b>{driftingRedCount}</b> project(s) drifting red — 3 consecutive days without recovering to green.
                  Look for these in the list below (marked with the drifting badge).
                </div>
              </section>
            )}

            {/* Iter 253z-15 · Send test digest banner (admin only) */}
            {isAdmin && (
              <section className="border border-border p-3 flex flex-wrap items-center gap-2" data-testid="pi-test-digest-banner">
                <Mail className="w-4 h-4 text-primary" />
                <b className="text-xs">Preview the digest</b>
                <span className="text-[10px] text-muted-foreground">Fire a one-off copy to any email address to test provider setup + layout.</span>
                <div className="flex items-center gap-1 ml-auto">
                  <Input
                    type="email"
                    placeholder="you@company.com"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    className="h-8 w-56 text-xs"
                    data-testid="pi-test-digest-email"
                  />
                  <Button size="sm" variant="outline" onClick={sendTest} disabled={testingSend} className="rounded-none h-8 text-xs" data-testid="pi-test-digest-send">
                    {testingSend ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
                    Send test
                  </Button>
                </div>
              </section>
            )}

            {/* Project cards */}
            <section className="space-y-3" data-testid="pi-project-list">
              {filtered.length === 0 ? (
                <div className="border border-dashed border-border p-8 text-center text-xs text-muted-foreground">No projects match this filter.</div>
              ) : filtered.map((p) => {
                const t = TRAFFIC[p.traffic] || TRAFFIC.green;
                const trow = trendMap[p.id];
                return (
                  <div key={p.id} className={`border ${t.tone} p-4 relative`} data-testid={`pi-project-${p.id}`}>
                    {/* R148g · Action buttons — Post Update · Tasks · History */}
                    <div className="absolute top-2 right-2 flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => setPostUpdateFor(p)}
                        className="inline-flex items-center gap-1 border border-primary/50 bg-primary/5 hover:bg-primary/10 text-primary px-2 py-0.5 text-[10px]"
                        title={`Post a status update on ${p.name}`}
                        data-testid={`pi-project-post-update-${p.id}`}
                      >
                        <MessageSquarePlus className="w-3 h-3" /> Post update
                      </button>
                      <button
                        type="button"
                        onClick={() => setTasksPopupFor(p)}
                        className="inline-flex items-center gap-1 border border-border bg-background/80 hover:bg-muted px-2 py-0.5 text-[10px]"
                        title={`See tasks for ${p.name}`}
                        data-testid={`pi-project-tasks-${p.id}`}
                      >
                        <ListChecks className="w-3 h-3" /> Tasks
                      </button>
                      <button
                        type="button"
                        onClick={() => setHistoryProject({ id: p.id, name: p.name })}
                        className="inline-flex items-center gap-1 border border-border bg-background/80 hover:bg-muted px-2 py-0.5 text-[10px]"
                        title={`View history for ${p.name}`}
                        data-testid={`pi-project-history-${p.id}`}
                      >
                        <History className="w-3 h-3" /> History
                      </button>
                    </div>
                    <div className="flex items-start gap-3 mb-2">
                      <span className={`w-2.5 h-2.5 rounded-full ${t.dot} mt-1.5 shrink-0`} title={t.label} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Link to={`/app/projects/${p.id}`} className="font-bold text-sm hover:underline">{p.name}</Link>
                          <span className={`chip text-[9px] ${t.chip}`}>{t.label.toUpperCase()}</span>
                          {p.has_update_today ? (
                            <span className="chip chip-approved text-[9px]">Updated today</span>
                          ) : (
                            <span className="chip chip-pending text-[9px]">No update yet</span>
                          )}
                          {p.status === "idle" && (
                            <span
                              className="chip text-[9px] border-slate-400 text-slate-700 bg-slate-100 cursor-help inline-flex items-center gap-1"
                              title={p.idle_reason
                                ? `Idle${p.idle_since ? " since " + new Date(p.idle_since).toLocaleDateString() : ""} — ${p.idle_reason}`
                                : "Idle — no reason provided. Set one from the project header."}
                              data-testid={`pi-project-idle-${p.id}`}
                            >
                              <MoonStar className="w-2.5 h-2.5" />
                              IDLE
                              {p.idle_reason ? (
                                <span className="normal-case font-normal truncate max-w-[16ch]">· {p.idle_reason}</span>
                              ) : (
                                <span className="normal-case font-normal italic opacity-70">· why?</span>
                              )}
                            </span>
                          )}
                          {trow?.drifting_red && (
                            <span className="chip chip-rejected text-[9px]" title="3 straight days without recovering to green">
                              <Flame className="w-2.5 h-2.5 inline mr-0.5" /> Drifting red
                            </span>
                          )}
                          <span className="text-[10px] text-muted-foreground">Owner: {p.owner_name || "—"}</span>
                          <span className="text-[10px] text-muted-foreground">· {p.days_without_update}d since last update</span>
                          {p.overdue_count > 0 && <span className="text-[10px] text-rose-700">· {p.overdue_count} overdue</span>}
                          {trow?.series?.length > 0 && (
                            <span className="inline-flex items-center gap-0.5 ml-1"
                                  title="7-day traffic (left = oldest)"
                                  data-testid={`pi-trend-${p.id}`}>
                              {trow.series.map((s) => {
                                const dotCls = s.traffic === "red" ? "bg-rose-500"
                                             : s.traffic === "amber" ? "bg-amber-500"
                                             : s.traffic === "green" ? "bg-emerald-500"
                                             : "bg-slate-300";
                                return <span key={s.date} className={`w-1.5 h-3 ${dotCls}`} title={`${s.date} · ${s.traffic || "no data"}`} />;
                              })}
                            </span>
                          )}
                        </div>
                        {p.ai_brief && (
                          <div className="text-xs italic mt-1.5 text-slate-700 border-l-2 border-primary/60 pl-2">
                            <Sparkles className="w-3 h-3 inline mr-1 text-primary" />{p.ai_brief}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mt-2 text-xs">
                      <ProjectSection title="Wins" icon={CheckCircle2} tone="emerald" items={p.wins.map((w) => `${w.title}${w.assignee ? " · " + w.assignee : ""}`)} empty="No wins today" />
                      <ProjectSection title="Losses" icon={TrendingDown} tone="rose" items={p.losses ? p.losses.map((l) => `${l.title || l.reason}${l.assignee ? " · " + l.assignee : ""}`) : []} empty="—" />
                      <ProjectSection title="Risks / Blockers" icon={Flame} tone="rose" items={[...(p.risks || []), ...(p.blockers || [])].slice(0, 5).map((b) => `${b.body}${b.author ? " — " + b.author : ""}`)} empty="—" />
                      <ProjectSection title="Alerts" icon={AlertTriangle} tone="amber" items={(p.alerts || []).map((a) => a.body)} empty="—" />
                    </div>
                    {(p.tickets_today?.length > 0 || p.hours_today > 0) && (
                      <div className="mt-2 pt-2 border-t border-border/50 flex flex-wrap gap-3 text-[10px] text-muted-foreground">
                        {p.hours_today > 0 && <span><b className="text-foreground">{p.hours_today}h</b> logged{Object.keys(p.hours_by_user_today || {}).length > 0 ? ` by ${Object.keys(p.hours_by_user_today).slice(0, 3).join(", ")}` : ""}</span>}
                        {p.tickets_today?.length > 0 && <span><b className="text-foreground">{p.tickets_today.length}</b> ticket(s){p.tickets_today.some((t) => t.sla_breached) ? " · SLA breached" : ""}</span>}
                      </div>
                    )}
                  </div>
                );
              })}
            </section>
          </>
        )}
      </div>

      {showConfig && <ConfigDialog wsId={activeWorkspaceId} onClose={() => setShowConfig(false)} initial={snap?.config} />}
      {showClientDigest && (
        <ClientDigestDialog
          wsId={activeWorkspaceId}
          projectsOfDay={snap?.projects || []}
          onClose={() => setShowClientDigest(false)}
        />
      )}
      {historyProject && (
        <ProjectHistoryDialog
          wsId={activeWorkspaceId}
          project={historyProject}
          onClose={() => setHistoryProject(null)}
          onJumpToDate={(d) => { setHistoryProject(null); onDateChange(d); }}
        />
      )}
      {showExecHistory && (
        <ExecSummaryHistoryDialog
          wsId={activeWorkspaceId}
          onClose={() => setShowExecHistory(false)}
          onJumpToDate={(d) => { setShowExecHistory(false); onDateChange(d); }}
        />
      )}
      {showCompare && (
        <CompareSnapshotsDialog
          wsId={activeWorkspaceId}
          historyDates={historyDates}
          onClose={() => setShowCompare(false)}
          onJumpToDate={(d) => { setShowCompare(false); onDateChange(d); }}
        />
      )}
      {postUpdateFor && (
        <PostProjectUpdateDialog
          project={postUpdateFor}
          onClose={() => setPostUpdateFor(null)}
          onPosted={() => load(false)}
        />
      )}
      {tasksPopupFor && (
        <ProjectTasksPopup
          project={tasksPopupFor}
          wsId={activeWorkspaceId}
          onClose={() => setTasksPopupFor(null)}
        />
      )}
    </>
  );
}


function Kpi({ label, value, icon: Icon, tone }) {
  const toneCls = tone === "green" ? "text-emerald-600"
                : tone === "amber" ? "text-amber-600"
                : tone === "red" ? "text-rose-600"
                : "text-muted-foreground";
  return (
    <div className="border border-border p-3 bg-background">
      <div className="label-eyebrow inline-flex items-center gap-1 mb-1">
        <Icon className={`w-3 h-3 ${toneCls}`} /> {label}
      </div>
      <div className="text-lg font-bold tabular-nums" style={{ fontFamily: "var(--font-heading)" }}>{value}</div>
    </div>
  );
}

function ProjectSection({ title, icon: Icon, tone, items, empty }) {
  const toneCls = tone === "emerald" ? "text-emerald-700"
                : tone === "rose" ? "text-rose-700"
                : tone === "amber" ? "text-amber-700"
                : "text-slate-700";
  return (
    <div>
      <div className={`label-eyebrow inline-flex items-center gap-1 mb-1 ${toneCls}`}>
        <Icon className="w-3 h-3" /> {title}
      </div>
      {items.length === 0 ? (
        <div className="text-[11px] text-muted-foreground italic">{empty}</div>
      ) : (
        <ul className="text-[11px] space-y-0.5 pl-3 list-disc">
          {items.slice(0, 6).map((s, i) => <li key={i}>{s}</li>)}
        </ul>
      )}
    </div>
  );
}

function MoverList({ title, icon: Icon, tone, items, empty, testid }) {
  const toneCls = tone === "emerald" ? "border-emerald-400 bg-emerald-50/40"
                : tone === "rose" ? "border-rose-400 bg-rose-50/40"
                : tone === "amber" ? "border-amber-400 bg-amber-50/40"
                : "border-border";
  const iconCls = tone === "emerald" ? "text-emerald-700"
                : tone === "rose" ? "text-rose-700"
                : tone === "amber" ? "text-amber-700"
                : "text-slate-700";
  return (
    <div className={`border ${toneCls} p-3`} data-testid={testid}>
      <div className={`label-eyebrow inline-flex items-center gap-1.5 mb-2 ${iconCls}`}>
        <Icon className="w-3.5 h-3.5" /> {title}
      </div>
      {items.length === 0 ? (
        <div className="text-[10px] text-muted-foreground italic">{empty}</div>
      ) : (
        <ul className="space-y-1.5">
          {items.map((it, i) => (
            <li key={i} className="text-[11px]">
              <div className="font-semibold truncate" title={it.title}>{it.title}</div>
              <div className="text-[10px] text-muted-foreground">{it.sub}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function ConfigDialog({ wsId, onClose, initial }) {
  const [cfg, setCfg] = useState(initial || {});
  const [busy, setBusy] = useState(false);
  const [recipientsStr, setRecipientsStr] = useState((initial?.extra_recipients || []).join(", "));

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/workspaces/${wsId}/project-intel/config`, {
        enabled: cfg.enabled ?? true,
        digest_pre_close_hour: Number(cfg.digest_pre_close_hour ?? 20),
        digest_final_hour: Number(cfg.digest_final_hour ?? 22),
        missed_penalty_points: Number(cfg.missed_penalty_points ?? -5),
        auto_task_enabled: !!cfg.auto_task_enabled,
        ai_brief_enabled: !!cfg.ai_brief_enabled,
        notify_project_owners: !!cfg.notify_project_owners,
        notify_workspace_owners: !!cfg.notify_workspace_owners,
        extra_recipients: recipientsStr.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success("Saved");
      onClose();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="pi-config-dialog">
        <DialogHeader><DialogTitle>Digest configuration</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })} data-testid="pi-cfg-enabled" />
            Enabled (both dashboard + emails)
          </label>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="label-eyebrow inline-block mb-1">Pre-close hour (local)</Label>
              <Input type="number" min="0" max="23" value={cfg.digest_pre_close_hour ?? 20} onChange={(e) => setCfg({ ...cfg, digest_pre_close_hour: e.target.value })} className="h-9" data-testid="pi-cfg-preclose-hour" />
            </div>
            <div>
              <Label className="label-eyebrow inline-block mb-1">Final hour (local)</Label>
              <Input type="number" min="0" max="23" value={cfg.digest_final_hour ?? 22} onChange={(e) => setCfg({ ...cfg, digest_final_hour: e.target.value })} className="h-9" data-testid="pi-cfg-final-hour" />
            </div>
          </div>
          <div>
            <Label className="label-eyebrow inline-block mb-1">Missed-update penalty (points, negative)</Label>
            <Input type="number" min="-100" max="0" value={cfg.missed_penalty_points ?? -5} onChange={(e) => setCfg({ ...cfg, missed_penalty_points: e.target.value })} className="h-9" data-testid="pi-cfg-penalty" />
          </div>
          <div>
            <Label className="label-eyebrow inline-block mb-1">Extra recipients (comma-separated)</Label>
            <Input placeholder="cxo@company.com, coo@company.com" value={recipientsStr} onChange={(e) => setRecipientsStr(e.target.value)} className="h-9" data-testid="pi-cfg-recipients" />
          </div>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={!!cfg.auto_task_enabled} onChange={(e) => setCfg({ ...cfg, auto_task_enabled: e.target.checked })} data-testid="pi-cfg-auto-task" />
            Auto-create &quot;Update status&quot; tasks at 10 PM
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={!!cfg.ai_brief_enabled} onChange={(e) => setCfg({ ...cfg, ai_brief_enabled: e.target.checked })} data-testid="pi-cfg-ai" />
            AI-narrated exec briefs (uses Emergent LLM key)
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={!!cfg.notify_project_owners} onChange={(e) => setCfg({ ...cfg, notify_project_owners: e.target.checked })} data-testid="pi-cfg-notify-po" />
            Send scoped digests to project owners
          </label>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={!!cfg.notify_workspace_owners} onChange={(e) => setCfg({ ...cfg, notify_workspace_owners: e.target.checked })} data-testid="pi-cfg-notify-wo" />
            Send full digests to workspace owners/admins
          </label>
        </div>
        <DialogFooter>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button className="rounded-none" onClick={save} disabled={busy} data-testid="pi-cfg-save">
            {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ClientDigestDialog({ wsId, projectsOfDay, onClose }) {
  const [step, setStep] = useState("select"); // select | preview | send
  const [projects, setProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [daysBack, setDaysBack] = useState(7);
  const [heading, setHeading] = useState("Weekly Project Update");
  const [brandColor, setBrandColor] = useState("#0f172a");
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewPayload, setPreviewPayload] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [emailsStr, setEmailsStr] = useState("");
  const [ccStr, setCcStr] = useState("");
  const [subject, setSubject] = useState("");
  const [intro, setIntro] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/projects`)
      .then((r) => setProjects((r.data || []).filter((p) => (p.status || "").toLowerCase() !== "archived")))
      .catch(() => setProjects([]));
  }, [wsId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return projects;
    return projects.filter((p) => (p.name || "").toLowerCase().includes(q)
                                  || (p.client_name || "").toLowerCase().includes(q));
  }, [projects, search]);

  const toggle = (pid) => {
    setSelected((prev) => prev.includes(pid) ? prev.filter((x) => x !== pid) : [...prev, pid]);
  };
  const selectAllVisible = () => setSelected(filtered.map((p) => p.id));
  const clearSel = () => setSelected([]);

  const doPreview = async () => {
    if (!selected.length) { toast.error("Select at least one project"); return; }
    setLoading(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/project-intel/client-digest/preview`, {
        project_ids: selected, days_back: daysBack, heading, brand_color: brandColor,
      });
      setPreviewHtml(data?.html || "");
      setPreviewPayload(data?.payload || null);
      setStep("preview");
    } catch (e) {
      toast.error("Preview failed");
    } finally { setLoading(false); }
  };

  const doDownloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.post(
        `/workspaces/${wsId}/project-intel/client-digest/export-pdf`,
        { project_ids: selected, days_back: daysBack, heading, brand_color: brandColor },
        { responseType: "blob" },
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `client-digest-${(previewPayload?.period_end) || "today"}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) { toast.error("PDF export failed"); }
    finally { setDownloading(false); }
  };

  const doSend = async () => {
    const tos = emailsStr.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    if (!tos.length) { toast.error("Add at least one recipient email"); return; }
    const ccs = ccStr.split(/[\s,;]+/).map((s) => s.trim()).filter(Boolean);
    setSending(true);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/project-intel/client-digest/send`, {
        project_ids: selected, days_back: daysBack, heading, brand_color: brandColor,
        to_emails: tos, cc_emails: ccs,
        subject: subject || undefined, intro_message: intro || undefined,
      });
      const okCount = (data?.results || []).filter((r) => r.ok).length;
      const failCount = (data?.results || []).length - okCount;
      if (failCount > 0) toast.warning(`Sent ${okCount}/${(data?.results || []).length} — ${failCount} failed`);
      else toast.success(`Sent to ${okCount} recipient(s)`);
      onClose?.();
    } catch (e) {
      toast.error("Send failed");
    } finally { setSending(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-4xl w-[92vw] max-h-[92vh] overflow-hidden flex flex-col" data-testid="pi-client-digest-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="w-4 h-4 text-primary" /> Client-facing project digest
          </DialogTitle>
        </DialogHeader>

        {/* Step chips */}
        <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest">
          {[
            { k: "select", label: "1 · Select projects" },
            { k: "preview", label: "2 · Preview" },
            { k: "send", label: "3 · Send" },
          ].map((s) => (
            <button
              key={s.k}
              type="button"
              onClick={() => (s.k === "preview" ? doPreview() : setStep(s.k))}
              disabled={s.k !== "select" && !selected.length}
              className={`px-2 py-1 border ${step === s.k ? "bg-foreground text-background border-foreground" : "border-border hover:bg-muted"} disabled:opacity-50`}
              data-testid={`pi-cd-step-${s.k}`}
            >
              {s.label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto -mx-6 px-6 space-y-4">
          {step === "select" && (
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label className="text-[10px] uppercase text-muted-foreground">Look-back window</Label>
                  <Select value={String(daysBack)} onValueChange={(v) => setDaysBack(Number(v))}>
                    <SelectTrigger className="h-9 text-xs" data-testid="pi-cd-days"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">Last 7 days</SelectItem>
                      <SelectItem value="14">Last 14 days</SelectItem>
                      <SelectItem value="21">Last 21 days</SelectItem>
                      <SelectItem value="30">Last 30 days</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-muted-foreground">Heading</Label>
                  <Input value={heading} onChange={(e) => setHeading(e.target.value)} className="h-9 text-xs" data-testid="pi-cd-heading" />
                </div>
                <div>
                  <Label className="text-[10px] uppercase text-muted-foreground">Brand color</Label>
                  <div className="flex items-center gap-1">
                    <input type="color" value={brandColor} onChange={(e) => setBrandColor(e.target.value)}
                            className="w-9 h-9 border border-border" data-testid="pi-cd-color" />
                    <Input value={brandColor} onChange={(e) => setBrandColor(e.target.value)} className="h-9 text-xs flex-1 font-mono" />
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <SearchIcon className="absolute left-2 top-2.5 w-3.5 h-3.5 text-muted-foreground" />
                  <Input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search projects…"
                    className="pl-8 h-9 text-xs"
                    data-testid="pi-cd-search"
                  />
                </div>
                <Button size="sm" variant="outline" onClick={selectAllVisible} data-testid="pi-cd-select-all">
                  Select all visible ({filtered.length})
                </Button>
                <Button size="sm" variant="ghost" onClick={clearSel} data-testid="pi-cd-clear">Clear</Button>
              </div>

              <div className="border border-border max-h-[45vh] overflow-y-auto divide-y divide-border" data-testid="pi-cd-projects-list">
                {filtered.length === 0 && (
                  <div className="p-4 text-xs text-muted-foreground">No projects match.</div>
                )}
                {filtered.map((p) => {
                  const on = selected.includes(p.id);
                  return (
                    <label key={p.id} className={`flex items-center gap-3 p-2 cursor-pointer text-xs ${on ? "bg-primary/5" : "hover:bg-muted/30"}`}
                            data-testid={`pi-cd-project-${p.id}`}>
                      <input
                        type="checkbox" checked={on} onChange={() => toggle(p.id)}
                        data-testid={`pi-cd-check-${p.id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium truncate">{p.name}</div>
                        <div className="text-[10px] text-muted-foreground truncate">
                          {[p.client_name, p.brand_name, (p.status || "").replace("_", " ")].filter(Boolean).join(" · ")}
                        </div>
                      </div>
                      {p.end_date && <span className="text-[10px] text-muted-foreground">ends {p.end_date}</span>}
                    </label>
                  );
                })}
              </div>
              <div className="text-[11px] text-muted-foreground">
                {selected.length} project(s) selected
              </div>
            </div>
          )}

          {step === "preview" && (
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground">
                Preview of the client-facing digest. This uses ONLY wins, milestones and highlights —
                no laggers, no penalties, no internal blockers.
              </div>
              <div className="border border-border bg-white" data-testid="pi-cd-preview">
                <iframe
                  title="Client digest preview"
                  srcDoc={previewHtml}
                  className="w-full min-h-[55vh]"
                  sandbox=""
                />
              </div>
            </div>
          )}

          {step === "send" && (
            <div className="space-y-3">
              <div>
                <Label className="text-[10px] uppercase text-muted-foreground">Recipient email(s)</Label>
                <Input value={emailsStr} onChange={(e) => setEmailsStr(e.target.value)}
                        placeholder="client1@acme.com, client2@acme.com"
                        className="h-9 text-xs" data-testid="pi-cd-emails" />
                <div className="text-[10px] text-muted-foreground mt-1">Comma or space separated.</div>
              </div>
              <div>
                <Label className="text-[10px] uppercase text-muted-foreground">CC (optional)</Label>
                <Input value={ccStr} onChange={(e) => setCcStr(e.target.value)}
                        placeholder="delivery@acme.com"
                        className="h-9 text-xs" data-testid="pi-cd-cc" />
              </div>
              <div>
                <Label className="text-[10px] uppercase text-muted-foreground">Subject</Label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)}
                        placeholder={`Weekly Project Update · ${previewPayload?.period_end || ""}`}
                        className="h-9 text-xs" data-testid="pi-cd-subject" />
              </div>
              <div>
                <Label className="text-[10px] uppercase text-muted-foreground">Intro message (optional)</Label>
                <textarea value={intro} onChange={(e) => setIntro(e.target.value)}
                           rows={3} placeholder="Hi team, sharing this week's progress."
                           className="w-full text-xs px-2 py-1 bg-background border border-border"
                           data-testid="pi-cd-intro" />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            {step === "preview" && (
              <Button variant="outline" size="sm" onClick={doDownloadPdf} disabled={downloading}
                       data-testid="pi-cd-pdf">
                {downloading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1.5" />}
                Download PDF
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>Close</Button>
            {step === "select" && (
              <Button size="sm" onClick={doPreview} disabled={loading || !selected.length}
                       data-testid="pi-cd-goto-preview">
                {loading ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <SearchIcon className="w-3.5 h-3.5 mr-1.5" />}
                Preview
              </Button>
            )}
            {step === "preview" && (
              <Button size="sm" onClick={() => setStep("send")} data-testid="pi-cd-goto-send">
                <Mail className="w-3.5 h-3.5 mr-1.5" /> Compose email
              </Button>
            )}
            {step === "send" && (
              <Button size="sm" onClick={doSend} disabled={sending}
                       className="bg-emerald-600 text-white hover:bg-emerald-500"
                       data-testid="pi-cd-send">
                {sending ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Send className="w-3.5 h-3.5 mr-1.5" />}
                Send digest
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


// ─── Feb 2026 · Snapshot history popups ─────────────────────

function ProjectHistoryDialog({ wsId, project, onClose, onJumpToDate }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!wsId || !project?.id) return;
    api.get(`/workspaces/${wsId}/project-intel/project-history`, {
      params: { project_id: project.id, limit: 60 },
    })
      .then((r) => setRows(r.data?.rows || []))
      .catch(() => setRows([]));
  }, [wsId, project?.id]);

  const trafficDot = (t) => t === "red" ? "bg-rose-500"
    : t === "amber" ? "bg-amber-500"
    : t === "green" ? "bg-emerald-500"
    : "bg-slate-300";

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="pi-project-history-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <History className="w-4 h-4 text-primary" />
            History · {project.name}
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto flex-1">
          {rows === null && <div className="p-6 text-sm text-muted-foreground">Loading history…</div>}
          {rows?.length === 0 && <div className="p-6 text-sm text-muted-foreground">No stored snapshots yet for this project. Snapshots are captured whenever the dashboard is refreshed or a digest is sent.</div>}
          {rows?.map((r) => (
            <div key={r.date} className="border-b border-border py-3 px-1" data-testid={`pi-history-row-${r.date}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`w-2 h-2 rounded-full ${trafficDot(r.traffic)}`} />
                <button className="font-bold text-sm underline hover:text-primary"
                        onClick={() => onJumpToDate(r.date)}
                        data-testid={`pi-history-jump-${r.date}`}>
                  {r.date}
                </button>
                <span className="text-[10px] text-muted-foreground">{r.hours_today}h · {r.wins_count} wins · {r.blockers_count} blockers · {r.overdue_count} overdue · {r.days_without_update}d silent</span>
              </div>
              {r.ai_brief && (
                <div className="text-xs italic mt-1.5 text-slate-700 border-l-2 border-primary/50 pl-2">
                  <Sparkles className="w-3 h-3 inline mr-1 text-primary" />{r.ai_brief}
                </div>
              )}
              {(r.wins?.length > 0 || r.blockers?.length > 0) && (
                <div className="grid grid-cols-2 gap-3 mt-2 text-[11px]">
                  {r.wins?.length > 0 && (
                    <div>
                      <div className="label-eyebrow text-emerald-700 mb-0.5">Wins</div>
                      <ul className="pl-3 list-disc">
                        {r.wins.slice(0, 3).map((w, i) => <li key={i}>{w.title || w.body}</li>)}
                      </ul>
                    </div>
                  )}
                  {r.blockers?.length > 0 && (
                    <div>
                      <div className="label-eyebrow text-rose-700 mb-0.5">Blockers</div>
                      <ul className="pl-3 list-disc">
                        {r.blockers.slice(0, 3).map((b, i) => <li key={i}>{b.body}</li>)}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ExecSummaryHistoryDialog({ wsId, onClose, onJumpToDate }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!wsId) return;
    api.get(`/workspaces/${wsId}/project-intel/exec-summary-history`, { params: { limit: 60 } })
      .then((r) => setRows(r.data?.rows || []))
      .catch(() => setRows([]));
  }, [wsId]);

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" data-testid="pi-exec-history-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <History className="w-4 h-4 text-primary" />
            Executive briefing · history
          </DialogTitle>
        </DialogHeader>
        <div className="overflow-y-auto flex-1">
          {rows === null && <div className="p-6 text-sm text-muted-foreground">Loading history…</div>}
          {rows?.length === 0 && <div className="p-6 text-sm text-muted-foreground">No stored executive summaries yet. Run <b>Rebuild + AI</b> a few times to build up a rolling history.</div>}
          {rows?.map((r) => (
            <div key={r.date} className="border-b border-border py-3 px-1" data-testid={`pi-exec-history-row-${r.date}`}>
              <div className="flex items-center gap-2">
                <button className="font-bold text-sm underline hover:text-primary"
                        onClick={() => onJumpToDate(r.date)}
                        data-testid={`pi-exec-history-jump-${r.date}`}>
                  {r.date}
                </button>
                {r.totals && <span className="text-[10px] text-muted-foreground">{r.totals.reds || 0}R / {r.totals.ambers || 0}A · {r.totals.updated_today || 0}/{r.totals.projects || 0} updated · {r.totals.wins || 0} wins</span>}
              </div>
              {r.headline && <div className="text-xs font-semibold mt-1">{r.headline}</div>}
              <div className="text-xs mt-1.5 whitespace-pre-wrap text-slate-700">{r.executive_summary}</div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Feb 2026 · Compare snapshots dialog (week-over-week) ──────

function CompareSnapshotsDialog({ wsId, historyDates = [], onClose, onJumpToDate }) {
  // Default: today vs 7 days ago (from historyDates if available)
  const todayDate = historyDates[0]?.date || "";
  const weekAgo = historyDates.find((d) => {
    if (!todayDate || !d.date) return false;
    return d.date <= new Date(new Date(todayDate).getTime() - 6 * 86400000).toISOString().slice(0, 10);
  })?.date || historyDates[Math.min(6, historyDates.length - 1)]?.date || "";
  const [dateA, setDateA] = useState(weekAgo);
  const [dateB, setDateB] = useState(todayDate);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState("wow");   // wow | custom

  const run = async (autoMode) => {
    setBusy(true);
    try {
      const useMode = autoMode || mode;
      const params = useMode === "wow" ? { mode: "wow" } : { mode: "custom", date_a: dateA, date_b: dateB };
      const { data } = await api.get(`/workspaces/${wsId}/project-intel/compare`, { params });
      setData(data);
      if (useMode === "wow") {
        setDateA(data.date_a); setDateB(data.date_b);
      }
    } catch (e) { toast.error(apiErrorMessage(e, "Compare failed")); }
    finally { setBusy(false); }
  };

  useEffect(() => { run("wow"); /* eslint-disable-next-line */ }, []);

  const trafficDot = (t) => t === "red" ? "bg-rose-500" : t === "amber" ? "bg-amber-500" : t === "green" ? "bg-emerald-500" : "bg-slate-300";

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col" data-testid="pi-compare-dialog">
        <DialogHeader>
          <DialogTitle className="inline-flex items-center gap-2">
            <GitCompareArrows className="w-4 h-4 text-primary" />
            Compare snapshots
          </DialogTitle>
        </DialogHeader>
        <div className="border border-border bg-muted/40 p-2 flex items-center gap-2 flex-wrap text-xs">
          <div className="inline-flex border border-border">
            {[["wow", "This week vs last week"], ["custom", "Pick two dates"]].map(([k, l]) => (
              <button key={k} onClick={() => { setMode(k); }} className={`px-3 py-1 uppercase text-[10px] tracking-widest font-semibold ${mode === k ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`} data-testid={`pi-compare-mode-${k}`}>{l}</button>
            ))}
          </div>
          {mode === "custom" && (
            <>
              <Label className="text-[10px]">Older</Label>
              <select value={dateA} onChange={(e) => setDateA(e.target.value)} className="h-8 border border-border px-2 text-xs bg-background" data-testid="pi-compare-a">
                <option value="">— Pick —</option>
                {historyDates.map((d) => <option key={d.date} value={d.date}>{d.date}</option>)}
              </select>
              <Label className="text-[10px]">Newer</Label>
              <select value={dateB} onChange={(e) => setDateB(e.target.value)} className="h-8 border border-border px-2 text-xs bg-background" data-testid="pi-compare-b">
                <option value="">— Pick —</option>
                {historyDates.map((d) => <option key={d.date} value={d.date}>{d.date}</option>)}
              </select>
            </>
          )}
          <Button size="sm" onClick={() => run()} disabled={busy || (mode === "custom" && (!dateA || !dateB))} className="rounded-none h-8 text-xs ml-auto" data-testid="pi-compare-run">
            {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <GitCompareArrows className="w-3 h-3 mr-1" />}
            Compare
          </Button>
        </div>
        <div className="overflow-y-auto flex-1 mt-2">
          {!data && !busy && <div className="p-6 text-center text-xs text-muted-foreground">Pick two dates above and hit Compare.</div>}
          {busy && !data && <div className="p-6 text-center text-xs text-muted-foreground">Loading comparison…</div>}
          {data?.missing?.length > 0 && (
            <div className="border border-amber-400 bg-amber-50/40 px-3 py-2 text-[11px] mb-2">
              No stored snapshot for: <b>{data.missing.join(", ")}</b>. Pick a different date or run &quot;Refresh + AI&quot; on those days first.
            </div>
          )}
          {data && !data.missing?.length && (
            <div className="space-y-3">
              {/* Totals delta */}
              <div className="grid grid-cols-3 md:grid-cols-6 gap-2">
                {["reds", "ambers", "greens", "wins", "blockers", "updated_today"].map((k) => {
                  const d = data.totals_delta?.[k] ?? 0;
                  const tone = k === "reds" || k === "blockers" ? (d > 0 ? "rose" : "emerald")
                             : (d > 0 ? "emerald" : d < 0 ? "rose" : "muted");
                  const cls = tone === "rose" ? "border-rose-400 text-rose-700"
                            : tone === "emerald" ? "border-emerald-400 text-emerald-700"
                            : "border-border text-muted-foreground";
                  return (
                    <div key={k} className={`border ${cls} p-2 text-[10px]`}>
                      <div className="label-eyebrow text-[9px] capitalize">{k.replace("_", " ")}</div>
                      <div className="text-lg font-bold">{d > 0 ? "+" : ""}{d}</div>
                      <div className="text-[9px] opacity-80">{data.totals_a?.[k] || 0} → {data.totals_b?.[k] || 0}</div>
                    </div>
                  );
                })}
              </div>
              <div className="text-[10px] text-muted-foreground">
                Comparing <button className="underline" onClick={() => onJumpToDate(data.date_a)}>{data.date_a}</button> → <button className="underline" onClick={() => onJumpToDate(data.date_b)}>{data.date_b}</button>
              </div>
              {/* Per-project diff */}
              <div className="border border-border divide-y divide-border">
                {(data.projects || []).map((p) => (
                  <div key={p.id} className="p-2.5" data-testid={`pi-compare-proj-${p.id}`}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`w-2 h-2 rounded-full ${trafficDot(p.traffic_a)}`} />
                      <span className="text-[10px]">→</span>
                      <span className={`w-2 h-2 rounded-full ${trafficDot(p.traffic_b)}`} />
                      <span className="font-semibold text-sm">{p.name}</span>
                      {p.traffic_changed && <span className="chip chip-warning text-[9px]">traffic changed</span>}
                      {!p.in_a && <span className="chip text-[9px] bg-emerald-50">new project</span>}
                      {!p.in_b && <span className="chip text-[9px] bg-rose-50">dropped</span>}
                      <span className="ml-auto text-[10px] text-muted-foreground">
                        {p.hours_today_a}h → {p.hours_today_b}h · overdue {p.overdue_a}→{p.overdue_b}
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-3 mt-1.5 text-[11px]">
                      {p.new_wins.length > 0 && (
                        <div>
                          <div className="label-eyebrow text-emerald-700 text-[9px] mb-0.5">New wins ({p.new_wins.length})</div>
                          <ul className="pl-3 list-disc">
                            {p.new_wins.slice(0, 3).map((w, i) => <li key={i}>{w.title || w.body}</li>)}
                          </ul>
                        </div>
                      )}
                      {p.new_blockers.length > 0 && (
                        <div>
                          <div className="label-eyebrow text-rose-700 text-[9px] mb-0.5">New blockers ({p.new_blockers.length})</div>
                          <ul className="pl-3 list-disc">
                            {p.new_blockers.slice(0, 3).map((b, i) => <li key={i}>{b.title || b.body}</li>)}
                          </ul>
                        </div>
                      )}
                      {p.resolved_blockers.length > 0 && (
                        <div>
                          <div className="label-eyebrow text-emerald-700 text-[9px] mb-0.5">Resolved blockers ({p.resolved_blockers.length})</div>
                          <ul className="pl-3 list-disc">
                            {p.resolved_blockers.slice(0, 3).map((b, i) => <li key={i}>{b.title || b.body}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                    {p.new_wins.length === 0 && p.new_blockers.length === 0 && p.resolved_blockers.length === 0 && (
                      <div className="text-[10px] text-muted-foreground italic mt-0.5">No wins / blockers deltas.</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

